import { Component, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  EMPTY,
  first,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { Constants } from 'src/app/core/constants';
import { AssignmentPriorities } from 'src/app/core/enums/assignment-priorities.enum';
import { AssignmentStatuses } from 'src/app/core/enums/assignment-statuses.enum';
import { ResourceTypes } from 'src/app/core/enums/resource-types.enum';
import { UserCompanyPermissions } from 'src/app/core/enums/user-company-permissions.enum';
import { Assignment, ProjectList } from 'src/app/core/models/project/project-list.interface';
import { ProjectsFilterForm } from 'src/app/core/models/project/projects-filter-form.model';
import {
  AssignmentView,
  ProjectView,
  TeamMemberView,
} from 'src/app/core/models/project/projects-view.interface';
import { RequestModel } from 'src/app/core/models/request-model.interface';
import { MinimalProfile } from 'src/app/core/models/user/profile';
import { EmployeeService } from 'src/app/core/services/employee.service';
import { FileService } from 'src/app/core/services/file.service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { ProjectService } from 'src/app/core/services/project.service';

@Component({
  selector: 'app-projects',
  templateUrl: './projects.component.html',
  styleUrls: ['./projects.component.scss'],
  providers: [ProjectsFilterForm],
  standalone: false,
})
export class ProjectsComponent implements OnDestroy {
  readonly filterForm = inject(ProjectsFilterForm);
  private readonly projectService = inject(ProjectService);
  private readonly fileService = inject(FileService);
  private readonly employeeService = inject(EmployeeService);
  private readonly permissionService = inject(PermissionService);
  private readonly translateService = inject(TranslateService);

  public readonly uaDateFormat = Constants.DateFormats.get('ua-date');
  public readonly currentLang: string;

  public readonly emptyProjectListParam: Record<string, string> = {
    uk: 'проєктів',
    en: 'project',
  };

  public readonly canCreateProject$ = this.permissionService.userPermissions$.pipe(
    map((permissions) =>
      this.permissionService.hasPermission(
        permissions,
        ResourceTypes.Project,
        UserCompanyPermissions.Create,
      ),
    ),
    shareReplay(),
  );

  public readonly canEditProject$ = this.permissionService.userPermissions$.pipe(
    map((permissions) =>
      this.permissionService.hasPermission(
        permissions,
        ResourceTypes.Project,
        UserCompanyPermissions.Edit,
      ),
    ),
    shareReplay(),
  );

  public uiProjects$ = new Observable<ProjectView[]>();
  public companyId = '';
  public isCreateProjectModalOpen = false;

  private readonly assignmentPreviewCount = 3;
  private readonly teamPreviewCount = 4;
  private readonly defaultAvatarImage = Constants.NotFound;
  private readonly expandedTaskProjects = new Set<string>();
  private readonly expandedTeamProjects = new Set<string>();
  private readonly projectsState$ = new BehaviorSubject<ProjectList[]>([]);

  private request: RequestModel = {
    page: 1,
    pageSize: 1000,
    filters: [
      {
        field: 'isArchived',
        operator: 'eq',
        value: 'false',
        values: [],
      },
    ],
  };

  private initUrls$: Observable<Params>;
  private unsubscribe$ = new Subject<void>();

  constructor() {
    const route = inject(ActivatedRoute);

    this.initUrls$ = route.parent!.parent!.params.pipe(
      first(),
      tap((params) => {
        const companyId = params['id'];
        this.companyId = companyId;

        this.projectService.setProjectsUrl(companyId);
        this.employeeService.setEmployeesUrl(companyId);
      }),
    );

    this.uiProjects$ = this.projectsState$.pipe(
      map((projects) => projects.map((project) => this.buildProjectView(project))),
      shareReplay(1),
    );

    this.filterForm.form.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$),
        startWith(this.filterForm.form.getRawValue()),
        tap((formValue) => {
          const isArchivedFilter = this.request.filters.find((f) => f.field === 'isArchived');

          if (isArchivedFilter) {
            isArchivedFilter.value = String(formValue.isArchived);
          }

          this.loadProjects(this.initUrls$);
        }),
      )
      .subscribe();

    this.currentLang = this.translateService.currentLang ?? Constants.DefaultLanguage;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public onArchive(projectId: string): void {
    this.projectService
      .archiveProject(projectId)
      .pipe(
        first(),
        tap(() => {
          this.loadProjects(this.initUrls$);
        }),
      )
      .subscribe();
  }

  public onUnarchive(projectId: string): void {
    this.projectService
      .unarchiveProject(projectId)
      .pipe(
        first(),
        tap(() => {
          this.loadProjects(this.initUrls$);
        }),
      )
      .subscribe();
  }

  public toggleTaskExpanded(projectId: string): void {
    if (this.expandedTaskProjects.has(projectId)) {
      this.expandedTaskProjects.delete(projectId);
    } else {
      this.expandedTaskProjects.add(projectId);
    }

    this.projectsState$.next([...this.projectsState$.value]);
  }

  public toggleTeamExpanded(projectId: string): void {
    if (this.expandedTeamProjects.has(projectId)) {
      this.expandedTeamProjects.delete(projectId);
    } else {
      this.expandedTeamProjects.add(projectId);
    }

    this.projectsState$.next([...this.projectsState$.value]);
  }

  public openCreateProjectModal(): void {
    this.isCreateProjectModalOpen = true;
  }

  public closeCreateProjectModal(): void {
    this.isCreateProjectModalOpen = false;
  }

  public onCreateProjectSuccess(): void {
    this.isCreateProjectModalOpen = false;
    this.loadProjects(this.initUrls$);
  }

  private buildProjectView(project: ProjectList): ProjectView {
    const tagClass = this.mapTagClass(project.tag);
    const tagLabel = project.tag ? (Constants.ProjectTags.get(project.tag) ?? project.tag) : '';

    const assignments = project.assignments ?? [];
    const completedAssignmentsCount = project.completedAssignmentsCount ?? 0;
    const activeAssignmentsCount = project.activeAssignmentsCount ?? 0;
    const overdueAssignmentsCount = project.overdueAssignmentsCount ?? 0;
    const newAssignmentsCount = project.newAssignmentsCount ?? 0;
    const postponedAssignmentsCount = project.postponedAssignmentsCount ?? 0;

    const allTeamMembers = [...(project.employeeProfiles ?? []), ...(project.hiddenEmployees ?? [])];

    const openAssignments = [...assignments]
      .sort((left, right) => this.compareAssignments(left, right))
      .filter((assignment) => this.isOpenAssignmentStatus(this.getDisplayAssignmentStatus(assignment)))
      .map((assignment) => this.buildAssignmentView(assignment, allTeamMembers));

    const isTaskExpanded = this.expandedTaskProjects.has(project.id);
    const visibleAssignments = isTaskExpanded
      ? openAssignments
      : openAssignments.slice(0, this.assignmentPreviewCount);

    const canExpandAssignments = openAssignments.length > this.assignmentPreviewCount;
    const hiddenAssignmentsCount = Math.max(openAssignments.length - this.assignmentPreviewCount, 0);

    const teamMembers = allTeamMembers.map((member) => this.buildTeamMemberView(member));
    const isTeamExpanded = this.expandedTeamProjects.has(project.id);
    const previewTeamMembers = teamMembers.slice(0, this.teamPreviewCount);
    const fullTeamMembers = isTeamExpanded ? teamMembers : [];
    const canExpandTeam = teamMembers.length > this.teamPreviewCount;
    const hiddenTeamMembersCount = Math.max(teamMembers.length - this.teamPreviewCount, 0);

    return {
      id: project.id,
      title: project.title,
      tagLabel,
      tagClass,
      description: project.description,
      isArchived: project.isArchived,
      archiveButtonClass: project.isArchived
        ? 'archive-toggle-button archive-toggle-button--archived'
        : 'archive-toggle-button archive-toggle-button--default',
      completedAssignmentsCount,
      activeAssignmentsCount,
      overdueAssignmentsCount,
      newAssignmentsCount,
      postponedAssignmentsCount,
      visibleAssignments,
      hasVisibleAssignments: openAssignments.length > 0,
      canExpandAssignments,
      hiddenAssignmentsCount,
      isTaskExpanded,
      previewTeamMembers,
      fullTeamMembers,
      hasExpandedTeamMembers: fullTeamMembers.length > 0,
      teamMembersCount: teamMembers.length,
      hiddenTeamMembersCount,
      hasTeamMembers: teamMembers.length > 0,
      canExpandTeam,
      isTeamExpanded,
    };
  }

  private buildAssignmentView(
    assignment: Assignment,
    teamMembers: MinimalProfile[],
  ): AssignmentView {
    const status = this.getDisplayAssignmentStatus(assignment);
    const employee = teamMembers.find((member) => member.id === assignment.employeeId);

    return {
      id: assignment.id,
      title: assignment.title,
      employeeName: employee?.fullName || employee?.email || '',
      deadline: assignment.deadline,
      status,
      badgeClass: this.mapTaskBadgeClass(status),
      statusLabelKey: this.mapStatusLabelKey(status),
      borderColorHex: this.mapTaskBorderColor(status),
    };
  }

  private buildTeamMemberView(member: MinimalProfile): TeamMemberView {
    const fullName = member.fullName || member.email || '';
    const imageUrl = member.imageUrl || this.defaultAvatarImage;
    const hasImage = imageUrl !== this.defaultAvatarImage;

    return {
      id: member.id,
      fullName,
      imageUrl,
      initials: this.extractInitials(fullName),
      hasImage,
    };
  }

  private loadProjects(initUrls$: Observable<Params>): void {
    initUrls$
      .pipe(
        switchMap(() =>
          combineLatest([
            this.projectService.getProjects(this.request),
            this.employeeService.getCompanyEmployeesExtendedLookup(),
          ]),
        ),
        switchMap(([projects, employeesLookup]) =>
          forkJoin(
            projects.map((project) => {
              const employees = employeesLookup
                .filter((employee) => employee.projectIds.includes(project.id))
                .map((employee) => ({
                  id: employee.id,
                  fullName: employee.fullName,
                  imageUrl: employee.imageUrl,
                  avatarImageExtension: employee.avatarImageExtension,
                }));

              if (!employees.length) {
                return of({ ...project, employeeProfiles: [], hiddenEmployees: [] });
              }

              const employeeProfiles$ = forkJoin(
                employees
                  .slice(0, Constants.DefaultLoadedProjectEmployees)
                  .map((profile) => this.getEmployeeProfileWithImage$(profile)),
              );

              const hiddenEmployees$ =
                employees.length > Constants.DefaultLoadedProjectEmployees
                  ? forkJoin(
                      employees
                        .slice(Constants.DefaultLoadedProjectEmployees)
                        .map((profile) => this.getEmployeeProfileWithImage$(profile)),
                    )
                  : of([]);

              return forkJoin({
                employeeProfiles: employeeProfiles$,
                hiddenEmployees: hiddenEmployees$,
              }).pipe(
                map(({ employeeProfiles, hiddenEmployees }) => ({
                  ...project,
                  employeeProfiles,
                  hiddenEmployees,
                })),
              );
            }),
          ),
        ),
        first(),
        tap((projects) => {
          this.projectsState$.next(projects);
        }),
        catchError((error) => {
          console.error(error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private getEmployeeProfileWithImage$(profile: MinimalProfile): Observable<MinimalProfile> {
    if (!profile.avatarImageExtension) {
      return of({ ...profile, imageUrl: this.defaultAvatarImage });
    }

    return this.fileService.getEntityWithImageUrl(
      profile,
      `users/${profile.id}/avatar${profile.avatarImageExtension}`,
    );
  }

  private mapTagClass(tag?: string): string {
    if (!tag) {
      return 'project-tag-default';
    }

    const normalizedTag = String(tag).toLowerCase();

    if (normalizedTag.includes('frontend')) {
      return 'project-tag-frontend';
    }

    if (normalizedTag.includes('backend')) {
      return 'project-tag-backend';
    }

    if (normalizedTag.includes('design')) {
      return 'project-tag-design';
    }

    return 'project-tag-default';
  }

  private mapTaskBadgeClass(status: AssignmentStatuses | null): string {
    switch (status) {
      case AssignmentStatuses.Completed:
        return 'task-badge task-badge-completed';
      case AssignmentStatuses.Active:
        return 'task-badge task-badge-active';
      case AssignmentStatuses.Overdue:
        return 'task-badge task-badge-overdue';
      case AssignmentStatuses.New:
        return 'task-badge task-badge-new';
      case AssignmentStatuses.Postponed:
        return 'task-badge task-badge-postponed';
      default:
        return 'task-badge task-badge-default';
    }
  }

  private mapStatusLabelKey(status: AssignmentStatuses | null): string {
    switch (status) {
      case AssignmentStatuses.New:
        return 'ASSIGNMENT_STATUSES.NEW';
      case AssignmentStatuses.Completed:
        return 'ASSIGNMENT_STATUSES.COMPLETED';
      case AssignmentStatuses.Active:
        return 'ASSIGNMENT_STATUSES.ACTIVE';
      case AssignmentStatuses.Postponed:
        return 'ASSIGNMENT_STATUSES.POSTPONED';
      case AssignmentStatuses.Overdue:
        return 'ASSIGNMENT_STATUSES.OVERDUE';
      default:
        return 'ASSIGNMENT_STATUSES.ACTIVE';
    }
  }

  private mapTaskBorderColor(status: AssignmentStatuses | null): string {
    switch (status) {
      case AssignmentStatuses.Completed:
        return '#22c55e';
      case AssignmentStatuses.Active:
        return '#3b82f6';
      case AssignmentStatuses.Overdue:
        return '#ef4444';
      case AssignmentStatuses.New:
        return '#f59e0b';
      case AssignmentStatuses.Postponed:
        return '#6b7280';
      default:
        return '#334155';
    }
  }

  private isOpenAssignmentStatus(status: AssignmentStatuses | null): boolean {
    return (
      status === AssignmentStatuses.Active
      || status === AssignmentStatuses.New
      || status === AssignmentStatuses.Overdue
      || status === AssignmentStatuses.Postponed
    );
  }

  private compareAssignments(left: Assignment, right: Assignment): number {
    const urgencyDifference =
      this.getAssignmentUrgencyOrder(left) - this.getAssignmentUrgencyOrder(right);

    if (urgencyDifference !== 0) {
      return urgencyDifference;
    }

    const priorityDifference =
      this.getAssignmentPriorityOrder(right.priority) - this.getAssignmentPriorityOrder(left.priority);

    if (priorityDifference !== 0) {
      return priorityDifference;
    }

    const deadlineDifference =
      this.getAssignmentDateValue(right.deadline) - this.getAssignmentDateValue(left.deadline);

    if (deadlineDifference !== 0) {
      return deadlineDifference;
    }

    return this.getAssignmentDateValue(right.createdAt) - this.getAssignmentDateValue(left.createdAt);
  }

  private getAssignmentUrgencyOrder(assignment: Assignment): number {
    switch (this.getDisplayAssignmentStatus(assignment)) {
      case AssignmentStatuses.Overdue:
        return 0;
      case AssignmentStatuses.Active:
        return 1;
      case AssignmentStatuses.New:
        return 2;
      case AssignmentStatuses.Postponed:
        return 3;
      case AssignmentStatuses.Completed:
        return 4;
      default:
        return 99;
    }
  }

  private getDisplayAssignmentStatus(assignment: Assignment): AssignmentStatuses | null {
    const normalizedStatus = this.normalizeAssignmentStatus(assignment.status);

    if (
      (normalizedStatus === AssignmentStatuses.Active || normalizedStatus === AssignmentStatuses.New)
      && assignment.deadline
      && new Date(assignment.deadline).getTime() < Date.now()
    ) {
      return AssignmentStatuses.Overdue;
    }

    return normalizedStatus;
  }

  private getAssignmentPriorityOrder(priority: AssignmentPriorities | null | undefined): number {
    switch (priority) {
      case AssignmentPriorities.Critical:
        return 4;
      case AssignmentPriorities.High:
        return 3;
      case AssignmentPriorities.Middle:
        return 2;
      case AssignmentPriorities.Low:
        return 1;
      default:
        return 0;
    }
  }

  private getAssignmentDateValue(date: Date | string | undefined): number {
    if (!date) {
      return Number.MIN_SAFE_INTEGER;
    }

    const time = new Date(date).getTime();
    return Number.isNaN(time) ? Number.MIN_SAFE_INTEGER : time;
  }

  private extractInitials(name: string): string {
    const normalizedName = name.trim();
    if (!normalizedName) {
      return '?';
    }

    const parts = normalizedName.split(' ').filter(Boolean);
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  private normalizeAssignmentStatus(status: unknown): AssignmentStatuses | null {
    if (status === null || status === undefined) {
      return null;
    }

    if (typeof status === 'number') {
      if (status === AssignmentStatuses.New) {
        return AssignmentStatuses.New;
      }

      if (status === AssignmentStatuses.Completed) {
        return AssignmentStatuses.Completed;
      }

      if (status === AssignmentStatuses.Active) {
        return AssignmentStatuses.Active;
      }

      if (status === AssignmentStatuses.Postponed) {
        return AssignmentStatuses.Postponed;
      }

      if (status === AssignmentStatuses.Overdue) {
        return AssignmentStatuses.Overdue;
      }

      return null;
    }

    const normalized = String(status).toLowerCase();

    if (normalized === 'new') {
      return AssignmentStatuses.New;
    }

    if (normalized === 'active' || normalized === 'inprogress') {
      return AssignmentStatuses.Active;
    }

    if (normalized === 'completed') {
      return AssignmentStatuses.Completed;
    }

    if (normalized === 'postponed') {
      return AssignmentStatuses.Postponed;
    }

    if (normalized === 'overdue') {
      return AssignmentStatuses.Overdue;
    }

    return null;
  }
}
